import {
  useCallback, useId, useLayoutEffect, useRef, useState,
  type CSSProperties, type ReactNode,
} from "react";
import { useEmbedTokens } from "../theme";

// A modal dialog on the native <dialog> element.
//
// Why native: showModal() puts the dialog in the browser's TOP LAYER, so it
// escapes every stacking context the host page (Framer) creates and makes the
// rest of the page inert, with focus trapping and Escape handling built in.
// It is rendered inline, not portaled: the top layer needs no portal, and an
// inline dialog stays inside the account subtree that the sign-in gate
// unmounts, so a 401 while a dialog is open removes the dialog with it.
//
// Scrim (the DS colour scrim): inline styles cannot reach ::backdrop, and the
// embed injects no host CSS and must not need a CSP `style-src` exception. So
// the <dialog> itself fills the viewport and carries the scrim colour, and an
// inner panel is the visible dialog. A click whose target is the <dialog>
// element is therefore a backdrop click. The UA ::backdrop still paints below
// it, at the browser's own faint default.
//
// jsdom implements no showModal()/close(): the component falls back to the
// `open` attribute there, so unit tests see the rendered content but not top
// layer, inertness or focus trapping. Those are proven in a real browser by
// the embed primitives' browser spec (smoke-embed-primitives.spec.ts).

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  size?: "small" | "medium" | "large";
  role?: "dialog" | "alertdialog";
  /** Where focus goes on close. Falls back to whatever was focused at open. */
  returnFocusTo?: HTMLElement | null;
  /** Ignore Escape and backdrop clicks (a request is in flight). */
  closeDisabled?: boolean;
  footer?: ReactNode;
  children: ReactNode;
}

export interface UseEmbedDialog {
  open: boolean;
  returnFocusTo: HTMLElement | null;
  openFrom: (trigger: HTMLElement | null) => void;
  close: () => void;
}

/**
 * Open-state plus the trigger to return focus to. Pass the click handler's
 * `e.currentTarget` to `openFrom`: WebKit does not focus a <button> on click,
 * so reading `document.activeElement` at open would return focus to <body>.
 */
export function useEmbedDialog(): UseEmbedDialog {
  const [open, setOpen] = useState(false);
  const [returnFocusTo, setReturnFocusTo] = useState<HTMLElement | null>(null);
  const openFrom = useCallback((trigger: HTMLElement | null) => { setReturnFocusTo(trigger); setOpen(true); }, []);
  const close = useCallback(() => setOpen(false), []);
  return { open, returnFocusTo, openFrom, close };
}

function showDialog(dlg: HTMLDialogElement): void {
  if (typeof dlg.showModal === "function") {
    if (!dlg.open) dlg.showModal();
  } else {
    dlg.setAttribute("open", "");
  }
}

function hideDialog(dlg: HTMLDialogElement): void {
  if (typeof dlg.close === "function") {
    try { dlg.close(); } catch { /* already closed */ }
  }
  dlg.removeAttribute("open");
}

export function EmbedDialog({
  open, onClose, title, description, size = "medium", role = "dialog",
  returnFocusTo, closeDisabled = false, footer, children,
}: Props) {
  if (!open) return null;
  return (
    <OpenDialog onClose={onClose} title={title} description={description} size={size} role={role}
      returnFocusTo={returnFocusTo} closeDisabled={closeDisabled} footer={footer}>
      {children}
    </OpenDialog>
  );
}

// Mounted only while open, so a closed dialog keeps no hidden form state or
// duplicate ids in the page, and unmounting removes it from the top layer.
function OpenDialog({
  onClose, title, description, size, role, returnFocusTo, closeDisabled, footer, children,
}: Required<Pick<Props, "onClose" | "title" | "size" | "role" | "closeDisabled" | "children">> & Pick<Props, "description" | "returnFocusTo" | "footer">) {
  const t = useEmbedTokens();
  const titleId = useId();
  const descId = useId();
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const panelElRef = useRef<HTMLDivElement | null>(null);
  // The panel's BORDER-box width. useContainerWidth reports the content box,
  // which is padding narrower than the panel and so is always below
  // dialogSmall for a size="small" panel; comparing that to dialogSmall would
  // stack the footer at every viewport.
  const [panelWidth, setPanelWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = panelElRef.current;
    if (!el) return;
    setPanelWidth(el.offsetWidth);
    if (typeof window.ResizeObserver !== "function") return;
    const ro = new window.ResizeObserver(() => setPanelWidth(el.offsetWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Listeners read these through refs so the effect that owns the native
  // listeners does not re-run (and re-open the dialog) on every render.
  const onCloseRef = useRef(onClose);
  const closeDisabledRef = useRef(closeDisabled);
  const returnRef = useRef(returnFocusTo);
  onCloseRef.current = onClose;
  closeDisabledRef.current = closeDisabled;
  returnRef.current = returnFocusTo;

  useLayoutEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    showDialog(dlg);

    // Escape: the browser would close the dialog behind React's back, so take
    // the event and let the owner decide.
    const onCancel = (e: Event) => {
      e.preventDefault();
      if (!closeDisabledRef.current) onCloseRef.current();
    };
    // Drag-safe backdrop click: a press inside the panel (a text field, say)
    // released on the scrim produces a click whose target is the common
    // ancestor, the dialog, so the click alone cannot tell a backdrop click
    // from a drag. Require the pointerdown AND the click on the dialog itself.
    let downOnBackdrop = false;
    const onPointerDown = (e: PointerEvent) => { downOnBackdrop = e.target === dlg; };
    const onClick = (e: MouseEvent) => {
      const backdrop = downOnBackdrop && e.target === dlg;
      downOnBackdrop = false;
      if (backdrop && !closeDisabledRef.current) onCloseRef.current();
    };
    // A second Escape (or a close-watcher gesture) closes natively even though
    // `cancel` was prevented. Re-sync the owner, and re-show if it declined.
    const onNativeClose = () => {
      if (closeDisabledRef.current) {
        // Re-show in the same task so no frame paints the dialog non-modal.
        if (dlg.isConnected && !dlg.open) showDialog(dlg);
        return;
      }
      onCloseRef.current();
      requestAnimationFrame(() => { if (dlg.isConnected && !dlg.open) showDialog(dlg); });
    };
    dlg.addEventListener("cancel", onCancel);
    dlg.addEventListener("pointerdown", onPointerDown);
    dlg.addEventListener("click", onClick);
    dlg.addEventListener("close", onNativeClose);

    return () => {
      dlg.removeEventListener("cancel", onCancel);
      dlg.removeEventListener("pointerdown", onPointerDown);
      dlg.removeEventListener("click", onClick);
      dlg.removeEventListener("close", onNativeClose);
      // Read focus BEFORE closing: dialog.close() itself restores focus to
      // whatever held it before showModal, so afterwards the active element is
      // that opener (or body) and says nothing about where focus was inside
      // the dialog. Focus only moves if it was still inside (or nowhere).
      const before = document.activeElement;
      // A NATIVE close (a later Escape, a close watcher, dialog.close()) has already made the
      // browser put focus back on the element that held it at showModal() by the time the owner
      // unmounts us, so focus is then on the opener, outside the dialog. While the modal was open
      // nothing outside it could hold focus, so "on the opener" still means "ours to place"
      // (TD-577 (2)): without this, returnFocusTo was ignored after a native close.
      const focusWasOurs = before === null || before === document.body || dlg.contains(before) || before === openerRef.current;
      // Then close: while a modal dialog is open everything outside it is
      // inert, and an inert element cannot take focus.
      hideDialog(dlg);
      const target = returnRef.current ?? openerRef.current;
      if (focusWasOurs && target && target.isConnected) {
        target.focus();
        // After a NATIVE close, react-dom's commit-level selection restore
        // (getSelectionInformation/restoreSelection, commitRoot) re-focuses the
        // element the BROWSER focused when dialog.close() ran: it captures the
        // active element before this unmount commit — by then the browser has
        // already put focus on the opener — and re-focuses it after deletions
        // when it is still in the document (measured in chromium: our
        // target.focus() above lands, then react-dom calls focus() on the
        // opener in the same commit). React-owned closes are immune: the
        // captured element is inside the dialog being removed, so the
        // isInDocument guard skips the restore. One microtask later the commit
        // has fully settled — re-apply our target then, but only if focus is
        // still sitting on that captured element (never steal a later,
        // deliberate focus move).
        const clobberedTo = before;
        queueMicrotask(() => {
          if ((document.activeElement === clobberedTo || document.activeElement === document.body) && target.isConnected) target.focus();
        });
      }
    };
  }, []);

  const narrowFooter = panelWidth !== null && panelWidth > 0 && panelWidth < t.size.dialogSmall;
  const maxWidth = size === "small" ? t.size.dialogSmall : size === "large" ? t.size.dialogLarge : t.size.dialogMedium;

  const dialogStyle: CSSProperties = {
    position: "fixed", inset: 0, width: "100%", height: "100%", maxWidth: "none", maxHeight: "none",
    margin: 0, padding: 16, border: 0, boxSizing: "border-box", overflow: "auto",
    overscrollBehavior: "contain", background: t.colors.scrim, color: t.colors.text,
    display: "grid", placeItems: "center", fontFamily: t.fontFamily,
  };
  const panelStyle: CSSProperties = {
    background: t.colors.surface, borderRadius: t.borderRadiusLg, boxShadow: t.colors.overlayShadow,
    width: `min(100%, ${maxWidth}px)`, maxHeight: "calc(100dvh - 32px)", overflow: "auto",
    boxSizing: "border-box", padding: t.spacing.lg,
  };
  return (
    <dialog
      ref={dialogRef}
      data-aldelo-embed-dialog=""
      role={role === "alertdialog" ? "alertdialog" : undefined}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      style={dialogStyle}
    >
      <div ref={panelElRef} style={panelStyle}>
        <h2 id={titleId} style={{ margin: 0, fontSize: t.fontSize.lg, fontWeight: t.fontWeight.medium, lineHeight: t.lineHeight.tight, color: t.colors.text }}>
          {title}
        </h2>
        {description && (
          <p id={descId} style={{ margin: `${t.spacing.sm}px 0 0`, fontSize: t.fontSize.sm, lineHeight: t.lineHeight.normal, color: t.colors.textSecondary }}>
            {description}
          </p>
        )}
        <div style={{ marginTop: t.spacing.md }}>{children}</div>
        {footer && (
          <div style={{
            marginTop: t.spacing.lg, display: "flex", gap: t.spacing.sm,
            flexDirection: narrowFooter ? "column" : "row",
            justifyContent: narrowFooter ? "stretch" : "flex-end",
            alignItems: narrowFooter ? "stretch" : "center",
          }}>{footer}</div>
        )}
      </div>
    </dialog>
  );
}
